import React, { useState, useEffect, useMemo, memo } from 'react'
import TableOperationsUI from '../../../core-system/Table/TableOperationsUI'
import {
  filterRows,
  searchRows,
  sortRows,
} from '../../../core-system/Table/helperFunctions'
import { FilterCheckbox } from '../../../core-system/Table/FilterModel'
import { uniq, find } from 'lodash-es'
import { getProgramNamesFromType, getCommuterTagFilterOptions, COMMUTER_SEARCH_KEYS } from 'features/Segments/helperFunctions'

interface CommuterTableOperationsProps {
  commuters: any[]
  setFilteredCommuters: (rows: any[]) => void
}

const CommuterTableOperations = memo(
  ({ commuters, setFilteredCommuters }: CommuterTableOperationsProps) => {
    // Local state for displayed rows and filters/sort
    const [filterSet, setFilterSet] = useState(false)
    const [searchSet, setSearchSet] = useState(false)
    const [searchManipulatedRows, setSearchManipulatedRows] = useState<any[]>(
      []
    )
    const [filterManipulatedRows, setFilterManipulatedRows] = useState<any[]>(
      []
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
      null
    )
    const [searchValue, setSearchValue] = useState('')
    const [isFilterButtonActive, setIsFilterButtonActive] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)

    // Resetting everything if original commuters change
    // This is done because we have quick filters on the commuters table
    // and we want to reset the filters when the commuters change
    useEffect(() => {
      setSearchManipulatedRows([])
      setFilterManipulatedRows([])
      setSortDirection(null)
      setSearchSet(false)
      setFilterSet(false)
      setSearchValue('')
      setIsFilterButtonActive(false)
      setIsSearchActive(false)
    }, [commuters])

    // Get the final rows to display
    // getting the common rows between search, filter, and sort
    const finalRows = () => {
      // If nothing is set, return all rows
      if (!searchSet && !filterSet && !sortDirection) {
        return commuters
      }
      // If only sort is set, return sorted rows
      if (!searchSet && !filterSet && sortDirection) {
        return sortRows(commuters, 'name', sortDirection)
      }
      const commonRows = []
      // If any row is empty the result will be empty
      if (searchSet && searchManipulatedRows.length === 0) {
        return []
      }
      if (filterSet && filterManipulatedRows.length === 0) {
        return []
      }
      if (searchManipulatedRows.length)
        searchManipulatedRows.forEach((row) => {
          if (filterManipulatedRows.length) {
            if (
              find(filterManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      if (filterManipulatedRows.length)
        filterManipulatedRows.forEach((row) => {
          if (searchManipulatedRows.length) {
            if (
              find(searchManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      return sortRows(commonRows, 'name', sortDirection)
    }

    // Push up the current rows whenever they change
    useEffect(() => {
      setFilteredCommuters(finalRows())
    }, [searchManipulatedRows, filterManipulatedRows, sortDirection])

    // Build filter options
    const filters = useMemo(() => {
      const groups: string[] = []
      const programs: string[] = []
      const competitions: string[] = []

      commuters.forEach((c) => {
        if (c.segmentNames) groups.push(...c.segmentNames)
        if (c.programsEnrolled) programs.push(...c.programsEnrolled)
        if (c.competitionsEnrolled) competitions.push(...c.competitionsEnrolled)
      })
      const tagFilterSection = getCommuterTagFilterOptions(commuters)

      return [
        {
          header: 'Status',
          checkboxes: [
            {
              label: 'Registered',
              checked: false,
              key: 'hasRegistered',
              value: true,
            },
            { label: 'Invited', checked: false, key: 'hasInvite', value: true },
            {
              label: 'Needs Invite',
              checked: false,
              key: 'hasInvite',
              value: false,
            },
          ],
        },
        ...(tagFilterSection ? [tagFilterSection] : []),
        ...(groups.length
          ? [
              {
                header: 'Groups',
                checkboxes: uniq(groups).map((g) => ({
                  label: g,
                  checked: false,
                  key: 'segmentNames',
                  value: g,
                })),
              },
            ]
          : []),
        ...(programs.length
          ? [
              {
                header: 'Programs',
                checkboxes: uniq(programs).map((p) => ({
                  label: getProgramNamesFromType(p.toLowerCase()),
                  checked: false,
                  key: 'programsEnrolled',
                  value: p,
                })),
              },
            ]
          : []),
        ...(competitions.length
          ? [
              {
                header: 'Competitions',
                checkboxes: uniq(competitions).map((c) => ({
                  label: c,
                  checked: false,
                  key: 'competitionsEnrolled',
                  value: c,
                })),
              },
            ]
          : []),
      ]
    }, [commuters])

    // Handlers
    const handleApplyFilters = (filters: FilterCheckbox[]) => {
      const filtered = filterRows(commuters, filters)
      setFilterManipulatedRows(filtered)
      setFilterSet(true)
      setIsFilterButtonActive(true)
    }

    const handleFiltersReset = () => {
      setFilterManipulatedRows([])
      setFilterSet(false)
      setIsFilterButtonActive(false)
    }

    const handleSearch = (value: string) => {
      if (value) {
        const searched = searchRows(
          commuters,
          [...COMMUTER_SEARCH_KEYS],
          value
        )
        setSearchManipulatedRows(searched)
        setSearchSet(true)
        setSearchValue(value)
        setIsSearchActive(true)
      } else {
        setSearchManipulatedRows([])
        setSearchSet(false)
        setSearchValue('')
        setIsSearchActive(false)
      }
    }

    const handleSearchClear = () => {
      setSearchManipulatedRows([])
      setSearchSet(false)
      setSearchValue('')
      setIsSearchActive(false)
    }

    const handleSortAsc = () => {
      setSortDirection('asc')
    }

    const handleSortDesc = () => {
      setSortDirection('desc')
    }

    return (
      <TableOperationsUI
        handleSearch={handleSearch}
        handleSearchClear={handleSearchClear}
        onAscendingClick={handleSortAsc}
        onDescendingClick={handleSortDesc}
        filters={filters}
        handleApplyFilters={handleApplyFilters}
        handleFiltersReset={handleFiltersReset}
        isFilterButtonActive={isFilterButtonActive}
        activeSortDirection={sortDirection}
        isSearchActive={isSearchActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  CommuterTableOperations.displayName = 'CommuterTableOperations'
}

export default CommuterTableOperations
